import { type MetaFunction } from '@remix-run/node'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { EndCap } from './components/end-cap.tsx'
import { MarketingHero } from './components/marketing-hero.tsx'
import { MarketingSection } from './components/marketing-section.tsx'

export const meta: MetaFunction = () => [
	{ title: 'About — Momentum' },
	{
		name: 'description',
		content:
			'Momentum is a platform for designing and implementing progressive training programs. Built for coaches and athletes who think training deserves better infrastructure.',
	},
]

export default function AboutRoute() {
	return (
		<>
			<MarketingHero
				eyebrow="About"
				heading="Momentum is a platform for designing and implementing progressive training programs."
				subhead="Built for coaches and athletes who think training deserves better infrastructure than a shared spreadsheet."
				ctaLabel="Sign up and build momentum"
				ctaHref="/signup"
				variant="compact"
			/>

			<MarketingSection>
				<EyebrowLabel>What we believe</EyebrowLabel>
				<dl className="mt-12 space-y-12">
					<div>
						<dt className="font-display text-display-3">
							Communication Matters.
						</dt>
						<dd className="mt-3 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
							Coaches have style and so do athletes. Is it sets x reps or reps x
							sets? Is it RPE or percentage? A shared language is the foundation
							of good coaching and effective communication.
						</dd>
					</div>
					<div>
						<dt className="font-display text-display-3">
							Language has meaning.
						</dt>
						<dd className="mt-3 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
							A shared language between coach and athlete is itself a coaching
							tool. It resolves ambiguity around exactly what happened versus
							what was prescribed, and allows for more nuanced adjustments and
							feedback.
						</dd>
						<dd className="mt-3 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
							When training data is structured and standardized, it becomes
							computable. This unlocks a new level of insight into the training
							process for both coach and athlete.
						</dd>
					</div>
					<div>
						<dt className="font-display text-display-3">
							Progression is everything.
						</dt>
						<dd className="mt-3 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
							Progressive overload is the only way to get stronger, faster,
							fitter. Programs should be designed with progression in mind, and
							athletes should be able to track that progression over time.
						</dd>
						<dd className="mt-3 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
							When coaches can easily build progression into their programs and
							athletes can easily track it, everyone wins. Coaches can see
							what’s working and what’s not, and athletes can stay motivated by
							seeing their progress over time.
						</dd>
					</div>
				</dl>
			</MarketingSection>

			<MarketingSection tone="ink-soft">
				<EyebrowLabel>What's inside</EyebrowLabel>
				<p className="mt-6 max-w-3xl text-base leading-relaxed text-ink-fg-soft">
					A training platform built around a shared language of programming,
					with features that compound visibility and trust between coach and
					athlete. Planning and logging workouts, analyzing results, tracking
					progress over time, and communicating around it all in one place.
				</p>
				<ul className="mt-8 flex flex-wrap gap-2">
					<li className="mt-4">
						<span className="mt-1 h-2 w-2 text-nowrap rounded-full bg-ember-soft p-2">
							Workout Planning Tools
						</span>
					</li>
					<li className="mt-4">
						<span className="mt-1 h-2 w-2 text-nowrap rounded-full bg-ember-soft p-2">
							Client Management
						</span>
					</li>
					<li className="mt-4">
						<span className="mt-1 h-2 w-2 text-nowrap rounded-full bg-ember-soft p-2">
							Group and Team Management
						</span>
					</li>
					<li className="mt-4">
						<span className="mt-1 h-2 w-2 text-nowrap rounded-full bg-ember-soft p-2">
							Progress Tracking and Analytics
						</span>
					</li>
					<li className="mt-4">
						<span className="mt-1 h-2 w-2 text-nowrap rounded-full bg-ember-soft p-2">
							In-Depth Exercise Definitions
						</span>
					</li>
					<li className="mt-4">
						<span className="mt-1 h-2 w-2 text-nowrap rounded-full bg-ember-soft p-2">
							Custom Achievements and Milestones
						</span>
					</li>
					<li className="mt-4">
						<span className="mt-1 h-2 w-2 text-nowrap rounded-full bg-ember-soft p-2">
							In-app Communication Tools
						</span>
					</li>
				</ul>
			</MarketingSection>
			<EndCap />
		</>
	)
}
